/**
 * AJ Events — front-end event submission enhancements.
 *
 * Progressive enhancement over the plain [aj_events_submit] form:
 *   1. A light WYSIWYG (bold / italic / bullets) on the description, with a
 *      character limit, syncing to the underlying <textarea>.
 *   2. The "online event" checkbox hides the venue detail fields.
 *   3. The venue name field becomes an autocomplete of existing venues that
 *      fills in (and reuses) the saved venue address.
 *
 * Everything degrades gracefully: with JS off, the plain textarea + fields work.
 */

(() => {
  const cfg = window.AJ_EVENTS_SUBMIT || {};
  const form = document.querySelector('[data-aj-submit]');
  if (!form) return;

  initWysiwyg(form);
  initOnlineToggle(form);
  initVenueLookup(form, cfg);

  /* ---- 1. WYSIWYG ------------------------------------------------------- */
  function initWysiwyg(scope) {
    const ta = scope.querySelector('textarea[data-aj-wys]');
    if (!ta || !document.queryCommandSupported) return;

    const max = parseInt(ta.getAttribute('maxlength'), 10) || 2000;
    ta.removeAttribute('maxlength');
    ta.removeAttribute('required'); // Enforced on the editor instead.

    const wrap = document.createElement('div');
    wrap.className = 'aj-ev-wys';
    wrap.innerHTML =
      '<div class="aj-ev-wys__bar">'
      + '<button type="button" class="aj-ev-wys__btn" data-cmd="bold" title="Bold" aria-label="Bold"><strong>B</strong></button>'
      + '<button type="button" class="aj-ev-wys__btn" data-cmd="italic" title="Italic" aria-label="Italic"><em>I</em></button>'
      + '<button type="button" class="aj-ev-wys__btn" data-cmd="insertUnorderedList" title="Bulleted list" aria-label="Bulleted list">&bull; List</button>'
      + '</div>'
      + '<div class="aj-ev-wys__edit" contenteditable="true" role="textbox" aria-multiline="true"></div>'
      + '<div class="aj-ev-wys__count" aria-hidden="true"></div>';
    ta.parentNode.insertBefore(wrap, ta);
    ta.style.display = 'none';

    const edit = wrap.querySelector('.aj-ev-wys__edit');
    const count = wrap.querySelector('.aj-ev-wys__count');
    edit.setAttribute('data-placeholder', ta.getAttribute('placeholder') || '');
    if (ta.value) edit.innerHTML = ta.value;

    const len = () => edit.textContent.length;
    const selLen = () => {
      const sel = window.getSelection();
      return sel && !sel.isCollapsed ? sel.toString().length : 0;
    };
    const sync = () => {
      ta.value = len() ? edit.innerHTML : '';
      count.textContent = `${len()} / ${max}`;
      count.classList.toggle('is-over', len() > max);
    };
    sync();

    wrap.querySelectorAll('[data-cmd]').forEach((btn) => {
      btn.addEventListener('mousedown', (e) => e.preventDefault()); // Keep the selection.
      btn.addEventListener('click', () => {
        edit.focus();
        document.execCommand(btn.getAttribute('data-cmd'), false, null);
        sync();
      });
    });

    edit.addEventListener('beforeinput', (e) => {
      if (e.inputType && e.inputType.indexOf('delete') === 0) return;
      const adding = e.data ? e.data.length : 1;
      if (len() - selLen() + adding > max) e.preventDefault();
    });
    edit.addEventListener('paste', (e) => {
      e.preventDefault();
      const text = ((e.clipboardData || window.clipboardData).getData('text') || '');
      const room = max - (len() - selLen());
      if (room > 0) document.execCommand('insertText', false, text.slice(0, room));
    });
    edit.addEventListener('input', sync);

    form.addEventListener('submit', (e) => {
      if (!edit.textContent.trim()) {
        e.preventDefault();
        wrap.classList.add('is-error');
        edit.focus();
      }
    });
  }

  /* ---- 2. Online toggle hides the venue ------------------------------- */
  function initOnlineToggle(scope) {
    const cb = scope.querySelector('[data-aj-online]');
    const fields = scope.querySelector('[data-aj-venuefields]');
    if (!cb || !fields) return;

    const apply = () => { fields.hidden = cb.checked; };
    cb.addEventListener('change', apply);
    apply();
  }

  /* ---- 3. Venue autocomplete ------------------------------------------ */
  function initVenueLookup(scope, config) {
    const input = scope.querySelector('[data-aj-venue-lookup]');
    if (!input || !config.restUrl) return;

    const hidden = scope.querySelector('[data-aj-venue-id]');
    const addr = scope.querySelector('[name="aj_ev_address"]');
    const city = scope.querySelector('[name="aj_ev_city"]');
    const state = scope.querySelector('[name="aj_ev_state"]');

    const row = input.closest('.aj-ev-submit__row') || input.parentNode;
    row.style.position = 'relative';
    const box = document.createElement('div');
    box.className = 'aj-ev-venue-results';
    box.hidden = true;
    row.appendChild(box);

    let timer;
    let last = '';

    input.setAttribute('autocomplete', 'off');
    input.addEventListener('input', () => {
      if (hidden) hidden.value = ''; // Typing means a new/edited venue until re-picked.
      const q = input.value.trim();
      clearTimeout(timer);
      if (q.length < 2) { close(); return; }
      timer = setTimeout(() => search(q), 250);
    });

    function search(q) {
      if (q === last) return;
      last = q;
      fetch(`${config.restUrl}venues/search?q=${encodeURIComponent(q)}`, {
        headers: config.nonce ? { 'X-WP-Nonce': config.nonce } : {},
      })
        .then((r) => (r.ok ? r.json() : []))
        .then(render)
        .catch(() => {});
    }

    function render(rows) {
      if (!Array.isArray(rows) || !rows.length) { close(); return; }
      box.innerHTML = rows
        .map((v, i) => {
          const place = v.city ? ` <span>· ${esc(v.city)}${v.state ? `, ${esc(v.state)}` : ''}</span>` : '';
          return `<button type="button" class="aj-ev-venue-opt" data-i="${i}">${esc(v.name)}${place}</button>`;
        })
        .join('');
      box.hidden = false;
      box.querySelectorAll('.aj-ev-venue-opt').forEach((btn) => {
        btn.addEventListener('click', () => pick(rows[parseInt(btn.dataset.i, 10)]));
      });
    }

    function pick(v) {
      if (!v) return;
      input.value = v.name || '';
      if (hidden) hidden.value = v.id || '';
      if (addr && v.address) addr.value = v.address;
      if (city && v.city) city.value = v.city;
      if (state && v.state) state.value = v.state;
      close();
    }

    function close() {
      box.hidden = true;
      box.innerHTML = '';
    }

    document.addEventListener('click', (e) => {
      if (e.target !== input && !box.contains(e.target)) close();
    });
  }

  function esc(s) {
    const d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
  }
})();
